import React, { useState, useContext } from 'react';
import styles from './styles.module.css';
import ShopSettingsService from 'services/ShopSettingsService';
import { AlertMessagesContext } from 'react-alert-messages';
import { useEffect } from 'react';
import DataTable from 'components/utils/DataTable/index';
import Info from 'components/utils/Alert/Info';
import { Button, TextField, Grid, CircularProgress } from '@mui/material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditPaymentModeDialog from 'components/popups/EditPaymentModeDialog';
import { useTranslation } from 'react-i18next';

function PaymentModes() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [loadingModeId, setLoadingModeId] = useState(null);
  const [paymentModes, setPaymentModes] = useState([]);
  const initialData = { name: '', order: '' };
  const [data, setData] = useState(initialData);
  const [selectedPaymentModeForEdit, setSelectedPaymentModeForEdit] = useState(null);

  const getPaymentModes = async () => {
    setLoadingIndicator(true);
    try {
      const params = { all: true };
      const res = await ShopSettingsService.getPaymentModes(params);
      setPaymentModes(res);
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'fail' });
    }
    setLoadingIndicator(false);
  };

  const checkDuplicateName = (name, ignoreId = null) => {
    return paymentModes.some(
      (mode) => mode.id !== ignoreId && mode.name.trim().toUpperCase() === name.trim().toUpperCase()
    );
  };

  const updatePaymentModeStatus = (mode) => {
    const data = { is_enabled: !mode?.is_enabled };

    // Optimistically update the list
    setPaymentModes((prev) => prev.map((m) => (m.id === mode.id ? { ...m, ...data } : m)));
    setLoadingModeId(mode.id);

    updatePaymentModes(mode, data);
  };

  const updatePaymentModes = async (mode, data) => {
    if (data.name) {
      if (checkDuplicateName(data.name, mode?.id)) {
        postAlertMessage({
          text: tManagement('Payment mode already exists'),
          type: 'fail',
        });
        return;
      }
    }

    setLoadingIndicator(true);
    try {
      await ShopSettingsService.updatePaymentModes(mode?.id, data);
      postAlertMessage({
        text: tManagement('Payment modes updated successfully'),
        type: 'success',
      });
      setSelectedPaymentModeForEdit(null);
      getPaymentModes();
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'fail' });
      // Rollback on failure if it was a status toggle
      if (loadingModeId === mode.id) {
        setPaymentModes((prev) => prev.map((m) => (m.id === mode.id ? { ...m, is_enabled: !data.is_enabled } : m)));
      }
    } finally {
      setLoadingIndicator(false);
      setLoadingModeId(null);
    }
  };

  const createPaymentMode = async () => {
    setLoadingIndicator(true);
    try {
      if (data?.name?.length) {
        const payload = { ...data };
        if (payload.order === '') {
          delete payload.order;
        }
        if (checkDuplicateName(data.name)) {
          postAlertMessage({
            text: tManagement('Payment mode already exists'),
            type: 'fail',
          });
          setLoadingIndicator(false);
          return;
        }
        await ShopSettingsService.createPaymentMode(payload);
        postAlertMessage({
          text: tManagement('Payment modes added successfully'),
          type: 'success',
        });
        setData(initialData);
        getPaymentModes();
      } else {
        postAlertMessage({
          text: tManagement('Please provide a valid payment mode name'),
          type: 'fail',
        });
      }
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'fail' });
    }
    setLoadingIndicator(false);
  };

  const headerData = [
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Status'),
      id: 'staus',
      type: 'callback',
      viewRender: (obj) => {
        return obj.is_enabled ? tCommon('Active') : tCommon('Deactive');
      },
    },
    {
      label: tCommon('Order'),
      id: 'order',
      type: 'text',
    },
    {
      label: tCommon('Edit'),
      id: 'edit',
      type: 'button',
      title: tCommon('Edit'),
      clickHandler: setSelectedPaymentModeForEdit,
    },
    {
      id: 'deleteBtn',
      type: 'callback',
      align: 'right',
      viewRender: (obj) => (
        <Button
          onClick={(e) => {
            if (!loadingModeId) {
              updatePaymentModeStatus(obj);
            }
            e.stopPropagation();
          }}
          variant="contained"
          size="small"
          color="primary"
          disabled={loadingModeId === obj.id}
          style={{ minWidth: '100px' }}
        >
          {loadingModeId === obj.id ? (
            <CircularProgress size={20} color="inherit" />
          ) : obj.is_enabled ? (
            tCommon('Deactivate')
          ) : (
            tCommon('Activate')
          )}
        </Button>
      ),
    },
  ];

  const getRowStyle = (row) => {
    if (row.is_enabled) {
      return;
    }
    return { backgroundColor: 'rgba(245, 169, 39, 0.2)' };
  };

  useEffect(() => {
    getPaymentModes();
    // eslint-disable-next-line
  }, []);

  return (
    <div className={styles.contentLayout}>
      <div className={styles.settingsGrid}>
        <div className={styles.BillHeaderDiv}>
          <span className={styles.pageHead}>{tCommon('Create payment mode')}</span>
        </div>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              color="primary"
              label={tCommon('Enter payment mode name')}
              inputProps={{
                style: {
                  padding: 10,
                },
              }}
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value.toUpperCase() })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              color="primary"
              label={tCommon('Order')}
              inputProps={{
                style: {
                  padding: 10,
                },
              }}
              value={data.order}
              onChange={(e) => setData({ ...data, order: e.target.value === '' ? '' : Number(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              className={styles.fieldButtons}
              variant="contained"
              size="medium"
              style={{ backgroundColor: '#ff851b' }}
              onClick={createPaymentMode}
              disabled={loadingIndicator}
            >
              {loadingIndicator && !selectedPaymentModeForEdit ? (
                <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
              ) : (
                <NoteAddIcon className={styles.addBtnIcon} />
              )}
              {loadingIndicator && !selectedPaymentModeForEdit ? tCommon('Creating...') : tCommon('Create')}
            </Button>
          </Grid>
        </Grid>
        {paymentModes.length ? (
          <DataTable columns={headerData} rows={paymentModes} rowStyle={getRowStyle} />
        ) : (
          !loadingIndicator && (
            <Info
              title={tManagement('No payments modes to list')}
              content={tManagement('You have no payments modes to list. Please create a new payment mode')}
            />
          )
        )}
      </div>
      {selectedPaymentModeForEdit && (
        <EditPaymentModeDialog
          handleClose={() => {
            setSelectedPaymentModeForEdit(null);
          }}
          paymentMode={selectedPaymentModeForEdit}
          updatePaymentModes={updatePaymentModes}
          isLoading={loadingIndicator}
        />
      )}
    </div>
  );
}

export default PaymentModes;
